import BasicForm, { basicFormButton, type FormButton } from "@/components/form/BasicForm"
import BootstrapButton from "@/components/form/BootstrapButton"
import Icon from "@/components/Icon"
import Modal from "@/components/Modal"
import type { ShipmentLog } from "@/models"
import DocumentsFileUpload from "@/shared/DocumentsFileUpload"
import SelectRecipients from "@/shared/SelectRecipients"
import usePopups from "@/stores/popupsStore"
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { ref, shallowRef } from "vue"
import { Alert, defineComponent, inferProps, ModalTitle, refSetter, Required, type ReactiveComponent } from "vue-utils"

interface Props {
	nextShipmentNumber: number
	recipientSuggestions: string[]

	createShipment(shipmentNumber: number, files: File[], recipients: string[]): Promise<ShipmentLog>
	cancel(): void
}

const NoRecipientsConfirm = (
	<Alert title="Confirm Create Shipment">
		<p>
			Please confirm you have uploaded <i>all</i> corresponding documentation, and no additional recipients were
			required.
		</p>
		<p>You will be using 1 of your shipment slots.</p>
	</Alert>
)
const WithRecipientsConfirm = (
	<Alert title="Confirm Document Upload">
		<p>
			Please confirm that the documents you have selected to upload are correct, and you have selected the correct
			recipients.
		</p>
		<p>
			You will not be able to delete the documents later, and an email will immediately be sent to all selected
			recipients.
		</p>
		<p>You will be using 1 of your shipment slots.</p>
	</Alert>
)

const CreateShipmentForm: ReactiveComponent<Props> = (props) => {
	const popups = usePopups()

	type Page = "files" | "recipients"
	const page = ref<Page>("files")

	const shipmentNo = ref(props.nextShipmentNumber)
	const files = shallowRef<File[]>([])
	const recipients = shallowRef<string[]>([])

	async function validateForm() {
		return await popups.confirm({
			content: () => (recipients.value.length === 0 ? NoRecipientsConfirm : WithRecipientsConfirm),
			width: "32rem",
			confirmText: "Create",
		})
	}

	async function handleCreate() {
		const hasRecipients = recipients.value.length > 0
		const recipientsText = recipients.value.length === 1 ? "recipient" : "recipients"

		const text = hasRecipients
			? `A new shipment was successfully created & an email sent to the selected ${recipientsText}`
			: "A new shipment was successfully created"

		const createdShipment = await props.createShipment(shipmentNo.value, files.value, recipients.value)
		void popups.showAlertPopup(() => (
			<Alert title="Successfully Created Shipment">
				{text}.
				<br />
				<br />
				The new shipment number is <strong>{createdShipment.shipmentNumber}</strong>
			</Alert>
		))
	}

	const backButton: FormButton = {
		id: "back",
		validateForm: true,
		onSubmit: () => void (page.value = "files"),
		render: (btnProps) => (
			<BootstrapButton color="secondary" {...btnProps}>
				<Icon icon={faArrowLeft} />
				Back
			</BootstrapButton>
		),
	}
	const cancelButton = basicFormButton({
		color: "secondary",
		id: "cancel",
		text: "Cancel",
		validateForm: false,
		onSubmit: props.cancel,
	})
	const nextButton: FormButton = {
		id: "back",
		validateForm: true,
		onSubmit: () => void (page.value = "recipients"),
		render: (btnProps) => (
			<BootstrapButton color="primary" {...btnProps} disabled={btnProps.disabled || files.value.length === 0}>
				Next
				<Icon icon={faArrowRight} />
			</BootstrapButton>
		),
	}
	const submitButton: FormButton = {
		id: "submit",
		render: (btnProps) => (
			<BootstrapButton color="primary" {...btnProps}>
				{recipients.value.length === 0 ? "Create" : "Create & Email"}
			</BootstrapButton>
		),
		validateForm: false,
		validate: validateForm,
		onSubmit: handleCreate,
	}

	const titles: Record<Page, string> = {
		files: "Select Documents to Upload",
		recipients: "Add Email Recipients",
	}
	const buttons: Record<Page, FormButton[]> = {
		files: [cancelButton, nextButton],
		recipients: [backButton, submitButton],
	}

	return () => (
		<Modal style={{ width: "60rem", height: "36rem" }}>
			<ModalTitle title={titles[page.value]} />
			<hr />
			<BasicForm buttons={buttons[page.value]} class="flex flex-col flex-grow-1">
				{page.value === "files" ? (
					<>
						<div class="mb-3">
							<label class="inline-flex">
								<Required label="Shipment Number" />
								<input type="number" min={props.nextShipmentNumber} v-model={shipmentNo.value} />
							</label>
						</div>
						<p class="mb-2">
							To create a shipment, you must first upload all documents related to the{" "}
							<strong>notification of shipment</strong>:
						</p>
						<DocumentsFileUpload class="flex-1" files={files.value} setFiles={refSetter(files)} required />
					</>
				) : (
					<SelectRecipients
						options={props.recipientSuggestions}
						recipients={recipients.value}
						setRecipients={refSetter(recipients)}
					/>
				)}
			</BasicForm>
		</Modal>
	)
}

export default defineComponent(CreateShipmentForm, inferProps<Props>())
